import * as React from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useQueryClient } from '@tanstack/react-query'
import { useSSEStream, type SSEConnectionState } from '@/hooks/useSSEStream'
import { usePermissions } from '@/hooks/usePermissions'
import type { CallLogResponseDTO } from '@/api/generated/models'
import { useTestCallTranscriptStore } from '@/stores/testCallTranscriptStore'
import { env } from '@/lib/env'

interface SSEContextValue {
  /** Current connection state */
  connectionState: SSEConnectionState
  /** Whether currently connected */
  isConnected: boolean
  /** Manually disconnect the stream */
  disconnect: () => void
}

const SSEContext = React.createContext<SSEContextValue | null>(null)

/**
 * Global SSE Provider - maintains a single SSE connection for the entire app
 *
 * This provider should be placed high in the component tree (e.g., in main.tsx)
 * so that the connection persists across route changes.
 */
export function SSEProvider({ children }: { children: React.ReactNode }) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0()
  const { hasAnyPermission } = usePermissions()
  const queryClient = useQueryClient()
  const [accessToken, setAccessToken] = React.useState<string>('')

  // Fetch the SSE access token once authenticated, retrying until it is available
  React.useEffect(() => {
    if (!isAuthenticated) return

    let cancelled = false
    let attempts = 0

    const fetchToken = () => {
      getAccessTokenSilently()
        .then(token => {
          if (!cancelled) setAccessToken(token)
        })
        .catch(error => {
          console.error('Failed to get access token for SSE:', error)
          if (!cancelled && attempts < 5) {
            attempts += 1
            setTimeout(fetchToken, 1000)
          }
        })
    }

    fetchToken()
    return () => {
      cancelled = true
    }
  }, [getAccessTokenSilently, isAuthenticated])

  /**
   * Handle SSE events and update React Query cache
   */
  const handleEvent = React.useCallback((eventType: string, data: any) => {
    console.log('Received SSE event:', eventType, data)

    switch (eventType) {
      case 'call_logs.created': {
        // Invalidate infinite query to trigger refetch
        // Use exact: false to match all variations of the infinite query
        console.log('Invalidating call_logs infinite queries after call_logs.created')
        queryClient.invalidateQueries({
          queryKey: ['calllog', 'infinite'],
          exact: false,  // Explicitly enable partial key matching
        })
        console.log('Invalidation complete')
        break
      }

      case 'call_logs.updated': {
        const callLog = data as CallLogResponseDTO

        // Invalidate infinite query to trigger refetch
        console.log('Invalidating call_logs infinite queries after call_logs.updated')
        queryClient.invalidateQueries({
          queryKey: ['calllog', 'infinite'],
          exact: false,  // Explicitly enable partial key matching
        })

        // Invalidate transcript if call log changed (may have new transcript)
        queryClient.invalidateQueries({ queryKey: ['calllog', 'transcript', callLog.id] })
        break
      }

      case 'transcripts.created': {
        // When transcript is created, invalidate to fetch initial state
        const transcript = data as any
        if (transcript.call_log_id) {
          queryClient.invalidateQueries({ queryKey: ['calllog', 'transcript', transcript.call_log_id] })
        }
        break
      }

      case 'transcripts.updated': {
        // Full transcript update - refetch entire transcript
        const transcript = data as any
        if (transcript.call_log_id) {
          queryClient.invalidateQueries({ queryKey: ['calllog', 'transcript', transcript.call_log_id] })
        }
        break
      }

      case 'transcripts.turn_added': {
        // Real-time turn streaming - incrementally update transcript
        const turn = data as any
        useTestCallTranscriptStore.getState().upsertTurn(turn)

        if (!turn.transcript_id) {
          console.warn('transcripts.turn_added event missing transcript_id:', turn)
          break
        }

        // Find which call log this transcript belongs to
        // We need to get the transcript from cache to find the call_log_id
        const transcriptQueries = queryClient.getQueriesData<any>({
          queryKey: ['calllog', 'transcript'],
          exact: false
        })

        for (const [queryKey, transcript] of transcriptQueries) {
          if (transcript && transcript.id === turn.transcript_id) {
            // Found the matching transcript - update it with the new turn
            queryClient.setQueryData(queryKey, (old: any) => {
              if (!old) return old

              // Check if this turn already exists (by id or turn_number) to prevent duplicates
              const existingTurns = old.turns || []
              const turnExists = existingTurns.some((t: any) =>
                (turn.id && t.id === turn.id) ||
                (turn.turn_number !== undefined && t.turn_number === turn.turn_number)
              )

              // Skip if turn already exists
              if (turnExists) {
                console.log('Turn already exists, skipping duplicate:', {
                  transcript_id: turn.transcript_id,
                  turn_number: turn.turn_number,
                  turn_type: turn.turn_type
                })
                return old
              }

              // Add the new turn to the turns array
              const updatedTurns = [...existingTurns, turn]

              // Update metadata counters
              const updatedTranscript = {
                ...old,
                turns: updatedTurns,
                total_turns: updatedTurns.length,
                total_user_turns: updatedTurns.filter((t: any) => t.turn_type === 'user_speech').length,
                total_agent_turns: updatedTurns.filter((t: any) => t.turn_type === 'agent_speech').length,
                total_function_calls: updatedTurns.filter((t: any) => t.turn_type === 'function_call').length,
              }

              console.log('Added turn to transcript:', {
                transcript_id: turn.transcript_id,
                turn_number: turn.turn_number,
                turn_type: turn.turn_type,
                total_turns: updatedTranscript.total_turns
              })

              return updatedTranscript
            })
            break
          }
        }
        break
      }

      case 'transcripts.turn_updated': {
        // Real-time turn update - patch existing turn in transcript cache
        const turn = data as any
        useTestCallTranscriptStore.getState().upsertTurn(turn)

        if (!turn.transcript_id) {
          console.warn('transcripts.turn_updated event missing transcript_id:', turn)
          break
        }

        const transcriptQueries = queryClient.getQueriesData<any>({
          queryKey: ['calllog', 'transcript'],
          exact: false
        })

        for (const [queryKey, transcript] of transcriptQueries) {
          if (transcript && transcript.id === turn.transcript_id) {
            queryClient.setQueryData(queryKey, (old: any) => {
              if (!old) return old

              const existingTurns = old.turns || []
              const turnIndex = existingTurns.findIndex((t: any) =>
                (turn.id && t.id === turn.id) ||
                (turn.turn_number !== undefined && t.turn_number === turn.turn_number)
              )

              if (turnIndex === -1) {
                console.warn('Turn not found for update, skipping:', {
                  transcript_id: turn.transcript_id,
                  turn_id: turn.id,
                  turn_number: turn.turn_number,
                })
                return old
              }

              const updatedTurns = [...existingTurns]
              updatedTurns[turnIndex] = { ...existingTurns[turnIndex], ...turn }

              return { ...old, turns: updatedTurns }
            })
            break
          }
        }
        break
      }

      case 'activities.created': {
        // New activity created - invalidate to refetch with new data
        queryClient.invalidateQueries({ queryKey: ['activities'] })
        queryClient.invalidateQueries({ queryKey: ['activity-stats'] })
        break
      }

      case 'activities.updated': {
        // Activity updated - invalidate to refetch updated data
        queryClient.invalidateQueries({ queryKey: ['activities'] })
        queryClient.invalidateQueries({ queryKey: ['activity-stats'] })
        break
      }

      case 'events.created': {
        // New calendar event/appointment created - invalidate to refetch
        queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
        queryClient.invalidateQueries({ queryKey: ['event-search'] })
        queryClient.invalidateQueries({ queryKey: ['event-search-infinite'] })
        break
      }

      case 'events.updated': {
        // Calendar event/appointment updated - invalidate to refetch
        queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
        queryClient.invalidateQueries({ queryKey: ['event-search'] })
        queryClient.invalidateQueries({ queryKey: ['event-search-infinite'] })
        break
      }

      case 'events.deleted': {
        // Calendar event/appointment deleted - invalidate to refetch
        queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
        queryClient.invalidateQueries({ queryKey: ['event-search'] })
        queryClient.invalidateQueries({ queryKey: ['event-search-infinite'] })
        break
      }

      case 'callbacks.created': {
        // New callback request created - invalidate to refetch
        queryClient.invalidateQueries({ queryKey: ['callbacks'] })
        break
      }

      case 'callbacks.status_changed': {
        // Callback status updated (pending → acknowledged, etc.) - invalidate to refetch
        queryClient.invalidateQueries({ queryKey: ['callbacks'] })
        break
      }

      case 'post_processing.completed': {
        const ppData = data as any
        if (ppData.call_log_id) {
          queryClient.invalidateQueries({
            queryKey: ['calllog', ppData.call_log_id, 'post-processing-logs'],
          })
          queryClient.invalidateQueries({
            queryKey: ['calllog', ppData.call_log_id],
          })
        }
        break
      }

      // TODO: Custom tool SSE events — enable once backend emits these event types
      case 'custom_tools.created':
      case 'custom_tools.deleted': {
        queryClient.invalidateQueries({ queryKey: ['custom-tools'], exact: false })
        break
      }
      case 'custom_tools.updated': {
        queryClient.invalidateQueries({ queryKey: ['custom-tools'], exact: false })
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'agents' && query.queryKey[2] === 'custom-tools',
        })
        break
      }

      default:
        console.warn('Unhandled SSE event type:', eventType)
    }
  }, [queryClient])

  /**
   * Handle SSE connection errors
   */
  const handleError = React.useCallback((error: any) => {
    console.error('SSE connection error:', error)
  }, [])

  // Connect to SSE stream - this stays open for the lifetime of the app
  // Backend filters topics per permission (call_logs/transcripts → call_log:view,
  // activities/events/callbacks → event:view); skip connecting when the user has neither
  const sseStream = useSSEStream({
    endpoint: `${env.API_BASE_URL}/api/v1/stream/events?topics=call_logs&topics=transcripts&topics=activities&topics=events&topics=callbacks&topics=custom_tools&topics=post_processing`,
    accessToken,
    onEvent: handleEvent,
    onError: handleError,
    enabled: !!accessToken && hasAnyPermission(['call_log:view', 'event:view']),
    eventTypes: [
      'call_logs.created',
      'call_logs.updated',
      'transcripts.created',
      'transcripts.updated',
      'transcripts.turn_added',
      'transcripts.turn_updated',
      'activities.created',
      'activities.updated',
      'events.created',
      'events.updated',
      'events.deleted',
      'callbacks.created',
      'callbacks.status_changed',
      'post_processing.completed',
      // TODO: Enable once backend emits custom tool SSE events
      'custom_tools.created',
      'custom_tools.updated',
      'custom_tools.deleted'
    ]
  })

  return (
    <SSEContext.Provider value={sseStream}>
      {children}
    </SSEContext.Provider>
  )
}

/**
 * Hook to access the global SSE connection state
 *
 * @example
 * ```tsx
 * const { isConnected, connectionState } = useSSE()
 * ```
 */
export function useSSE(): SSEContextValue {
  const context = React.useContext(SSEContext)
  if (!context) {
    throw new Error('useSSE must be used within an SSEProvider')
  }
  return context
}
